import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';

export default function EscalationMatrix() {
  const { getAuthHeaders } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    level: '',
    name: '',
    mobile_number: '',
    email: ''
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/escalation-contacts`, {
        headers: getAuthHeaders()
      });
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch escalation contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await axios.put(`${API_URL}/escalation-contacts/${editingContact.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Contact updated successfully');
      } else {
        await axios.post(`${API_URL}/escalation-contacts`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Contact created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      toast.error('Failed to save contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      level: contact.level,
      name: contact.name,
      mobile_number: contact.mobile_number,
      email: contact.email
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await axios.delete(`${API_URL}/escalation-contacts/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const resetForm = () => {
    setFormData({
      level: '',
      name: '',
      mobile_number: '',
      email: ''
    });
    setEditingContact(null);
  };

  return (
    <Sidebar>
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Escalation Matrix
            </h1>
            <p className="text-slate-600 mt-2">Manage escalation contacts for agencies</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="level">Escalation Level *</Label>
                  <Input
                    id="level"
                    value={formData.level}
                    onChange={(e) => setFormData({...formData, level: e.target.value})}
                    placeholder="e.g., Counselor, Project Head, Operations & Support"
                    required
                    data-testid="level-input"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile_number">Mobile Number *</Label>
                  <Input
                    id="mobile_number"
                    value={formData.mobile_number}
                    onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                    placeholder="+91-9876543210"
                    required
                    data-testid="mobile-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email ID *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="submit-contact-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                    {editingContact ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800 mb-6">
          <strong>Note:</strong> These contacts will be visible to all agencies for issue escalation.
        </div>

        {loading ? (
          <div className="text-center py-12">Loading contacts...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <table className="w-full" data-testid="escalation-contacts-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Level</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Name</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Mobile Number</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Email ID</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`contact-row-${contact.id}`}>
                    <td className="p-4 align-middle text-sm text-slate-700 font-medium">{contact.level}</td>
                    <td className="p-4 align-middle text-sm text-slate-700">{contact.name}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{contact.mobile_number}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{contact.email}</td>
                    <td className="p-4 align-middle text-sm">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(contact)}
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(contact.id)}
                          data-testid={`delete-contact-${contact.id}`}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {contacts.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No escalation contacts found. Add your first contact to get started.
              </div>
            )}
          </div>
        )}
      </div>
    </Sidebar>
  );
}